import { useEffect, useRef, useState } from 'react';

import axios from '@/lib/axios';
import { Building2, Loader2, X } from 'lucide-react';

import { SheetPickerList } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/hooks/useTranslations';
import { Company } from '@/types';
import { router } from '@inertiajs/react';
import { type ReactNode } from 'react';
import { toast } from 'sonner';

interface Props {
    userId: string;
    userCompanies: Company[];
    companies?: Company[];
    trigger?: ReactNode;
    /** Set to drive the modal from the page — see useControllableOpen. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function AssignCompany({ userId, userCompanies: initialUserCompanies = [], trigger, open: openProp, onOpenChange }: Props) {
    const { t } = useTranslations();
    const [isOpen, setIsOpen] = useControllableOpen(openProp, onOpenChange);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [crmOptions, setCrmOptions] = useState<Company[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>(() => initialUserCompanies.map((c) => c.id as string));
    const [selectedCompanies, setSelectedCompanies] = useState<Company[]>(() => [...initialUserCompanies]);

    const debouncedSearch = useDebounce(searchTerm, 300);
    // Ref snapshot used in the search effect so selected companies are always
    // merged into the dropdown list without making them a re-fetch trigger.
    const selectedCompaniesRef = useRef(selectedCompanies);
    useEffect(() => {
        selectedCompaniesRef.current = selectedCompanies;
    }, [selectedCompanies]);

    useEffect(() => {
        setSelectedIds(initialUserCompanies.map((c) => c.id as string));
        setSelectedCompanies([...initialUserCompanies]);
    }, [initialUserCompanies]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedIds(initialUserCompanies.map((c) => c.id as string));
            setSelectedCompanies([...initialUserCompanies]);
        }
    }, [isOpen, initialUserCompanies]);

    // Fetches while the sheet is open — the list is the sheet body now, not a popover.
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setCrmOptions([]);
            return;
        }

        let cancelled = false;

        void (async () => {
            setIsSearching(true);
            try {
                const query = debouncedSearch.trim();
                const res = await axios.get('/api/companies', {
                    params: { search: query || undefined, limit: 25 },
                });
                if (cancelled) return;
                const list: Company[] = (res.data?.companies ?? []) as Company[];
                // Merge in already-selected companies so they appear in the list
                // even if not returned by search. Use a ref to avoid adding
                // selectedCompanies as an effect dependency (which re-fetches on every toggle).
                const map = new Map<string, Company>(list.map((c) => [c.id as string, c]));
                selectedCompaniesRef.current.forEach((c) => {
                    if (!map.has(c.id as string)) map.set(c.id as string, c);
                });
                setCrmOptions(Array.from(map.values()));
            } catch {
                // silent
            } finally {
                if (!cancelled) setIsSearching(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, debouncedSearch]);

    const handleRemoveChip = (id: string) => {
        setSelectedIds((prev) => prev.filter((x) => x !== id));
        setSelectedCompanies((prev) => prev.filter((c) => (c.id as string) !== id));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const initialIds = initialUserCompanies.map((c) => c.id as string);
            const toAdd = selectedIds.filter((id) => !initialIds.includes(id));
            const toRemove = initialIds.filter((id) => !selectedIds.includes(id));

            await Promise.all([
                ...toAdd.map((id) => axios.post(`/api/users/${userId}/companies`, { company_id: id })),
                ...toRemove.map((id) => axios.delete(`/api/users/${userId}/companies/${id}`)),
            ]);

            toast.success(t('employees.forms.assign_company.messages.assignments_updated', 'Company assignments updated!'));
            setIsOpen(false);
            router.reload();
        } catch {
            toast.error(t('employees.forms.assign_company.messages.failed_update_assignments', 'Failed to update company assignments'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : openProp === undefined ? (
                // Only when the modal owns its open state: a page driving it
                // supplies its own buttons and must not get a stray one here.
                <ResponsiveModalTrigger asChild>
                    <Button variant="outline">
                        <Building2 className="mr-2 h-4 w-4" />
                        {t('employees.forms.assign_company.buttons.assign_employee_to_company', 'Assign to company')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}

            <ResponsiveModalContent size="lg">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('employees.forms.assign_company.labels.companies', 'Company Assignments')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'employees.forms.assign_company.description',
                            'Select or deselect companies to assign this employee. Click Save to apply changes.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-3">
                    {/* The companies are listed in the sheet rather than behind a
                        combobox: the search still drives the same server query. */}
                    <SheetPickerList
                        multiple
                        values={selectedIds}
                        onValuesChange={(ids) => {
                            const known = new Map([...crmOptions, ...selectedCompanies].map((company) => [company.id as string, company] as const));
                            setSelectedIds(ids);
                            setSelectedCompanies(ids.map((id) => known.get(id)).filter((company): company is Company => Boolean(company)));
                        }}
                        search={searchTerm}
                        onSearchChange={setSearchTerm}
                        loading={isSearching}
                        options={crmOptions.map((company) => ({
                            id: company.id as string,
                            label: company.name ?? '',
                            keywords: company.company_code ?? undefined,
                            badge: company.company_code ? (
                                <Badge variant="outline" className="text-xs">
                                    {company.company_code}
                                </Badge>
                            ) : null,
                        }))}
                        labels={{
                            search: t('employees.forms.assign_company.placeholders.search_companies', 'Search by name or code…'),
                            empty: t('employees.forms.assign_company.empty', 'No companies found.'),
                            loading: t('employees.forms.assign_company.loading', 'Searching…'),
                        }}
                    />

                    {selectedCompanies.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {selectedCompanies.map((company) => (
                                <Badge key={company.id as string} variant="secondary" className="flex items-center gap-1 pr-1">
                                    <Building2 className="h-3 w-3 shrink-0" />
                                    <span className="max-w-[160px] truncate">{company.name}</span>
                                    <button
                                        type="button"
                                        className="hover:text-destructive ml-0.5 rounded"
                                        onClick={() => handleRemoveChip(company.id as string)}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => setIsOpen(false)} disabled={saving}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
